import { useState, useEffect } from 'react';
import { ChartBar, Lightning, UserCheck, Cpu, ArrowLeft, Funnel } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authService } from '../../../services/auth.service';
import ReactECharts from 'echarts-for-react';

const COLORS = ['#0c1316ff', '#f97316', '#5ca18aff', '#b9ad77ff', '#86c3ffff', '#6440b8ff', '#b7a265ff', '#4affeaff', '#e17a8bff', '#84cc16'];

export const TokenAnalyticsView = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [features, setFeatures] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Filters
  const [modelFilter, setModelFilter] = useState<string>('all');
  const [featureFilter, setFeatureFilter] = useState<string>('all');

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [usageData, featureData] = await Promise.all([
        authService.getUsageSummary().catch(() => ({ users: [] })),
        authService.getUsageByFeature().catch(() => ({ features: [] }))
      ]);
      setUsers(usageData.users || []);
      setFeatures(featureData.features || []);
    } catch (err) {
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

  // Compute Models
  const modelTotals: Record<string, { total_tokens: number, call_count: number, provider: string }> = {};
  users.forEach((u) => {
    if (u.models) {
      Object.entries(u.models).forEach(([mName, mStats]: [string, any]) => {
        if (!modelTotals[mName]) {
          modelTotals[mName] = { total_tokens: 0, call_count: 0, provider: mStats.provider };
        }
        modelTotals[mName].total_tokens += (mStats.total_tokens || 0);
        modelTotals[mName].call_count += (mStats.call_count || 0);
      });
    }
  });

  const modelsList = Object.entries(modelTotals).map(([name, stats]) => ({
    name,
    ...stats
  })).sort((a, b) => b.total_tokens - a.total_tokens);

  // Available Filter Options
  const availableModels = Object.keys(modelTotals);
  const availableFeatures = features.map(f => f.feature);

  // Filtered Users List
  const filteredUsers = users.map(u => {
    let computedTokens = 0;
    
    if (modelFilter === 'all' && featureFilter === 'all') {
      computedTokens = u.total_tokens || 0;
    } else {
      // If we have filters, we need to sum up matching segments.
      // However, the backend aggregates models and features separately per user.
      // E.g., u.models has model breakdown, u.features has feature breakdown.
      // They don't cross-multiply (we don't have user->model->feature).
      // So if both are set, we take the minimum of the two as a heuristic,
      // or we just fall back to filtering by one if the other is 'all'.
      if (modelFilter !== 'all' && featureFilter !== 'all') {
        const mTokens = (u.models && u.models[modelFilter]) ? u.models[modelFilter].total_tokens : 0;
        const fTokens = (u.features && u.features[featureFilter]) ? u.features[featureFilter].total_tokens : 0;
        computedTokens = Math.min(mTokens, fTokens); // Heuristic
      } else if (modelFilter !== 'all') {
        computedTokens = (u.models && u.models[modelFilter]) ? u.models[modelFilter].total_tokens : 0;
      } else if (featureFilter !== 'all') {
        computedTokens = (u.features && u.features[featureFilter]) ? u.features[featureFilter].total_tokens : 0;
      }
    }

    return {
      ...u,
      filtered_tokens: computedTokens
    };
  }).filter(u => u.filtered_tokens > 0)
  .sort((a, b) => b.filtered_tokens - a.filtered_tokens)
  .slice(0, 10); // Top 10

  const filteredTotalTokens = filteredUsers.reduce((s, u) => s + u.filtered_tokens, 0);

  const userPieData = filteredUsers.map(u => ({
    name: u.name || u.email,
    value: u.filtered_tokens
  }));

  const featurePieData = features.map(f => ({
    name: f.feature,
    value: f.total_tokens
  })).sort((a, b) => b.value - a.value).slice(0, 10);

  const getPieOptions = (data: any[], seriesName: string) => ({
    tooltip: {
      trigger: 'item',
      backgroundColor: '#0f172a',
      borderColor: '#334155',
      textStyle: { color: '#f8fafc', fontSize: 12, fontFamily: 'Outfit, sans-serif' },
      padding: [8, 12],
      borderRadius: 8,
      formatter: (params: any) => `
        <div style="font-weight: bold; margin-bottom: 4px;">${params.name}</div>
        <div style="color: #cbd5e1;">${formatNumber(params.value)} tokens</div>
      `
    },
    color: COLORS,
    series: [
      {
        name: seriesName,
        type: 'pie',
        radius: ['55%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: { show: false },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 0
        },
        emphasis: {
          scale: true,
          scaleSize: 10,
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)'
          }
        },
        data: data
      }
    ]
  });

  if (isLoading) {
    return <div className="flex justify-center items-center h-64 text-slate-400">Loading analytics...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate('/admin/users')}
          className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <h1 className="text-2xl font-black text-slate-900 font-outfit flex items-center gap-2">
          <ChartBar size={28} className="text-accent" weight="fill" />
          Platform Token Analytics
        </h1>
      </div>

      {/* Main Panels Side by Side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        
        {/* Top 10 Users Panel */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col min-h-[400px]">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row justify-between gap-4">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <UserCheck size={18} className="text-accent" />
              Users — Most Tokens
            </h2>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-400">
                <Funnel size={16} weight="bold" />
              </div>
              <div className="relative">
                <select 
                  value={modelFilter}
                  onChange={(e) => setModelFilter(e.target.value)}
                  className="appearance-none pl-4 pr-8 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all cursor-pointer font-outfit"
                >
                  <option value="all">All Models</option>
                  {availableModels.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                  <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
              <div className="relative">
                <select 
                  value={featureFilter}
                  onChange={(e) => setFeatureFilter(e.target.value)}
                  className="appearance-none pl-4 pr-8 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-full focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent transition-all cursor-pointer font-outfit"
                >
                  <option value="all">All Features</option>
                  {availableFeatures.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-400">
                  <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 p-5 pt-0">
            <div className="flex items-start gap-6 h-[300px]">
              {userPieData.length > 0 ? (
                <>
                  <div className="relative flex-shrink-0" style={{ width: 210, height: 210, marginTop: 4 }}>
                    <ReactECharts 
                      option={getPieOptions(userPieData, 'Users')} 
                      style={{ width: '100%', height: '100%' }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Total</span>
                      <span className="text-sm font-black text-slate-800 font-outfit">
                        {formatNumber(filteredTotalTokens)}
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Legend */}
                  <div className="flex-1 flex flex-col gap-3 overflow-y-auto h-full pr-2 pb-4 pt-2">
                    {filteredUsers.map((u, i) => {
                      const color = COLORS[i % COLORS.length];
                      const pct = filteredTotalTokens > 0 ? (u.filtered_tokens / filteredTotalTokens) * 100 : 0;
                      return (
                        <div key={u.email} className="flex flex-col">
                          <div className="flex justify-between items-end mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                              <div className="flex flex-col">
                                <span className="text-[11px] font-bold text-slate-700 truncate max-w-[140px]" title={u.name || u.email}>
                                  {u.name || u.email}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-right">
                              <span className="text-xs font-bold text-slate-800 font-outfit">
                                {formatNumber(u.filtered_tokens)}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400 w-8 text-right">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5 ml-4" style={{ width: "calc(100% - 1rem)" }}>
                            <div
                              className="h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">No data matches these filters.</div>
              )}
            </div>
          </div>
        </div>

        {/* Features by Tokens */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col min-h-[400px]">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Lightning size={18} className="text-orange-500" />
              Features — Most Tokens
            </h2>
          </div>
          <div className="flex-1 p-5 pt-0">
            <div className="flex items-start gap-6 h-[300px]">
              {featurePieData.length > 0 ? (
                <>
                  <div className="relative flex-shrink-0" style={{ width: 210, height: 210, marginTop: 4 }}>
                    <ReactECharts 
                      option={getPieOptions(featurePieData, 'Features')} 
                      style={{ width: '100%', height: '100%' }}
                    />
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Total</span>
                      <span className="text-sm font-black text-slate-800 font-outfit">
                        {formatNumber(featurePieData.reduce((s, f) => s + f.value, 0))}
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Legend */}
                  <div className="flex-1 flex flex-col gap-3 overflow-y-auto h-full pr-2 pb-4 pt-2">
                    {features.map((f, i) => {
                      const color = COLORS[i % COLORS.length];
                      const totalFeat = featurePieData.reduce((s, f) => s + f.value, 0);
                      const pct = totalFeat > 0 ? (f.total_tokens / totalFeat) * 100 : 0;
                      return (
                        <div key={f.feature} className="flex flex-col">
                          <div className="flex justify-between items-end mb-1">
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                              <div className="flex flex-col">
                                <span className="text-[11px] font-bold text-slate-700 truncate max-w-[140px]" title={f.feature}>
                                  {f.feature}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-right">
                              <span className="text-xs font-bold text-slate-800 font-outfit">
                                {formatNumber(f.total_tokens)}
                              </span>
                              <span className="text-[10px] font-bold text-slate-400 w-8 text-right">
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5 ml-4" style={{ width: "calc(100% - 1rem)" }}>
                            <div
                              className="h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%`, backgroundColor: color }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">No feature data available.</div>
              )}
            </div>
          </div>
        </div>

        {/* Models by Tokens (Full width) */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col min-h-[400px] xl:col-span-2">
          <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Cpu size={18} className="text-emerald-500" />
              Models — Most Tokens
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-slate-50/50 sticky top-0 border-b border-slate-100 text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-center w-12">#</th>
                  <th className="px-4 py-3">Model</th>
                  <th className="px-4 py-3">Provider</th>
                  <th className="px-4 py-3 text-right">Calls</th>
                  <th className="px-4 py-3 text-right">Avg Tokens/Call</th>
                  <th className="px-4 py-3 text-right">Total Tokens</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {modelsList.map((m, i) => (
                  <tr key={m.name} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3 text-center text-slate-400 font-medium">{i + 1}</td>
                    <td className="px-4 py-3 font-bold text-slate-800">{m.name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs uppercase">{m.provider}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{formatNumber(m.call_count)}</td>
                    <td className="px-4 py-3 text-right text-slate-400 text-xs">
                      {m.call_count > 0 ? formatNumber(Math.round(m.total_tokens / m.call_count)) : 0}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-slate-900 font-outfit">
                      {formatNumber(m.total_tokens)}
                    </td>
                  </tr>
                ))}
                {modelsList.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-400">No model data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};
